import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDownload } from "../pages/DownloadWindow/DownloadContext";
import { gaEvent } from "../utils/gaAnalytics";

export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=ai.zerotyping";

// Same installer the footer's "Download for Windows" button points at.
export const WINDOWS_INSTALLER_URL =
  process.env.REACT_APP_INSTALLER_URL ||
  "https://zerotyping.ai/ztaupdate/ZeroTypingAI.msi";

// Triggers the browser's native download of the .msi, exactly like the footer's
// <a href> anchor does (starts the real download; not the streaming fetch).
const triggerWindowsDownload = () => {
  const a = document.createElement("a");
  a.href = WINDOWS_INSTALLER_URL;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/**
 * Detects the visitor's operating system from the user agent.
 * Returns one of: "windows" | "android" | "ios" | "mac" | "unknown".
 */
export const detectOS = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  const platform = (navigator.platform || "").toLowerCase();

  // Android must be checked before "linux"/"mac" since some UAs include those.
  if (ua.includes("android")) return "android";

  // iOS: iPhone/iPad/iPod. iPadOS 13+ masquerades as Mac, so also detect a
  // touch-capable "MacIntel" as iOS.
  if (
    /iphone|ipad|ipod/.test(ua) ||
    (platform === "macintel" && navigator.maxTouchPoints > 1)
  ) {
    return "ios";
  }

  if (ua.includes("win")) return "windows";
  if (ua.includes("mac")) return "mac";

  return "unknown";
};

/**
 * OS-aware label for a download button, e.g. "Download for Windows".
 * Falls back to "Download App" when the OS can't be determined.
 */
export const getDownloadLabel = () => {
  switch (detectOS()) {
    case "windows":
      return "Download for Windows";
    case "mac":
      return "Download for Mac";
    case "ios":
      return "Download for iPhone";
    case "android":
      return "Download for Android";
    default:
      return "Download App";
  }
};

/**
 * Returns a click handler for "Download App" buttons that adapts to the
 * visitor's OS instead of always routing to the /download page:
 *   - windows  -> start the .msi download + show the Windows install modal
 *                 (identical to the footer's "Download for Windows" button)
 *   - android  -> open the Google Play listing in a new tab
 *   - mac/ios  -> route to the /download page (native builds not wired yet)
 *   - unknown  -> route to the /download page (safe fallback)
 *
 * `location` is an optional label forwarded to analytics (e.g. "home_hero").
 */
export const useSmartDownload = () => {
  const download = useDownload();
  const navigate = useNavigate();

  return useCallback(
    (location = "unknown") => {
      const os = detectOS();

      switch (os) {
        case "windows":
          gaEvent("download_click", {
            platform: "windows",
            file_name: "ZeroTypingAI.msi",
            location,
          });
          // Start the real browser download, then show the modal — every click,
          // exactly like the footer's "Download for Windows" button.
          triggerWindowsDownload();
          download?.openInstallModal("windows");
          break;

        case "android":
          gaEvent("download_click", { platform: "android", location });
          window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer");
          break;

        case "mac":
        case "ios":
        default:
          gaEvent("download_click", { platform: os, location });
          navigate("/download");
          break;
      }
    },
    [download, navigate]
  );
};
